import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useEntitlement } from "@/contexts/EntitlementContext";
import { completeCheckoutAuth, confirmCheckoutSession } from "@/lib/api";
import { getAccessToken, setAccessToken } from "@/lib/authToken";
import { setStoredSubscribedPlan } from "@/lib/subscriptionStorage";

const POLL_MS = 1100;
const POLL_MAX = 28;

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Runs once per Stripe return (?checkout=success). Confirms the Checkout Session server-side
 * (so DB updates without waiting for webhooks), polls /auth/me until has_access, then clears URL params.
 */
export function CheckoutSuccessSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshEntitlement } = useEntitlement();
  const startedKeyRef = useRef(null);

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    if (qs.get("checkout") !== "success") return;

    const sessionId = (qs.get("session_id") || "").trim();
    const plan = qs.get("plan") || "";
    const dedupeKey = sessionId || `${location.pathname}|${plan}|success`;

    if (startedKeyRef.current === dedupeKey) return;
    startedKeyRef.current = dedupeKey;

    let cancelled = false;

    (async () => {
      const pathAtStart = location.pathname;
      const searchAtStart = location.search;
      try {
        if (plan === "weekly" || plan === "monthly" || plan === "annual") {
          setStoredSubscribedPlan(plan);
        }

        if (/^cs_(test|live)_/.test(sessionId)) {
          const hadToken = Boolean(getAccessToken());
          try {
            if (hadToken) {
              await confirmCheckoutSession(sessionId);
            } else {
              const auth = await completeCheckoutAuth(sessionId);
              if (auth?.access_token) {
                setAccessToken(auth.access_token);
                window.dispatchEvent(new CustomEvent("rareRoles:auth-changed"));
              }
            }
          } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            const msg =
              typeof detail === "string"
                ? detail
                : "Could not verify checkout yet.";
            if (status === 403) {
              toast.error("Email mismatch", { description: msg });
            } else if (!hadToken && status !== 401) {
              toast.error("Could not finish sign-in after checkout", { description: msg });
            } else if (status !== 401) {
              toast.message("Still syncing your subscription…", {
                description: "We’ll keep checking. You can refresh if this persists.",
              });
            }
          }
        }

        for (let i = 0; i < POLL_MAX && !cancelled; i++) {
          if (!getAccessToken()) {
            break;
          }
          const ok = await refreshEntitlement();
          if (ok) {
            break;
          }
          await sleep(POLL_MS);
        }
      } finally {
        if (cancelled) return;
        const next = new URLSearchParams(searchAtStart);
        next.delete("checkout");
        next.delete("session_id");
        next.delete("plan");
        const search = next.toString();
        navigate(
          {
            pathname: pathAtStart,
            search: search ? `?${search}` : "",
            hash: location.hash || "",
          },
          { replace: true }
        );
        startedKeyRef.current = null;
      }
    })();

    return () => {
      cancelled = true;
      startedKeyRef.current = null;
    };
  }, [location.search, location.pathname, location.hash, navigate, refreshEntitlement]);

  return null;
}
